import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { PASSENGER_AGE } from '../constants/package'

dayjs.extend(customParseFormat)

/**
 * Formats a date string using the specified format
 * @param dateString - Date to format
 * @param format - Optional format string (defaults to 'MMM D, YYYY')
 * @returns Formatted date string
 */
export function formatDate(dateString: string | Date | Dayjs, format: string = 'MMM D, YYYY') {
  if (!dateString) return ''

  const date = dayjs(dateString)
  if (!date.isValid()) return dateString.toString()

  return date.format(format)
}

/**
 * Formats flight date/time.
 * If the date is the same as the reference date, returns only time.
 * Otherwise returns date + time.
 */
export function formatFlightDateTime({
  date,
  referenceDate,
  dateFormat = 'MMM D, h:mm A',
  timeFormat = 'h:mm A',
}: {
  date: string | Date
  referenceDate?: Dayjs
  dateFormat?: string
  timeFormat?: string
}): string {
  if (!date) return ''

  const target = dayjs(date)

  if (!target.isValid()) return date.toString()

  if (referenceDate && referenceDate.isValid()) {
    const reference = dayjs(referenceDate)
    if (target.isSame(reference, 'day')) {
      return target.format(timeFormat)
    }
  }

  // Otherwise show date + time
  return target.format(dateFormat)
}

/**
 * Converts total minutes into "Xh Ym" format.
 * Example: 106 → "1h 46m"
 */
export function covertMinutesToHrs(minutes: number | undefined): string {
  if (minutes === undefined) return ''
  if (isNaN(minutes) || minutes < 0) return ''

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours}h ${remainingMinutes}m`
  } else if (hours > 0) {
    return `${hours}h`
  } else {
    return `${remainingMinutes}m`
  }
}

/**
 * Generates date picker props for a passenger's date of birth field.
 * - Primary passenger (isPrimary: true): must be 18+ years old
 * - Children (index >= adultsCount): must be born within the last 11 years
 * - Adults (index < adultsCount): must be at least 11 years old
 *
 * @param index - Passenger index in the list
 * @param adultsCount - Number of adult passengers
 * @param isPrimary - Whether this is the primary passenger (enforces 18+ rule)
 */
export const getPassengerDatePickerConfig = (
  index: number,
  adultsCount: number,
  isPrimary = false,
) => {
  const PRIMARY_CUTOFF = dayjs().subtract(PASSENGER_AGE.PRIMARY_MIN, 'year').toDate()
  const ADULT_CUTOFF = dayjs().subtract(PASSENGER_AGE.MINOR_CUTOFF, 'year').toDate()
  const CHILD_DEFAULT = dayjs().add(1, 'day').subtract(PASSENGER_AGE.MINOR_CUTOFF, 'year').toDate()

  if (isPrimary) {
    return {
      disableFutureDates: true,
      maxDate: PRIMARY_CUTOFF,
      defaultCalendarMonth: PRIMARY_CUTOFF,
    }
  }

  if (index >= adultsCount) {
    return {
      disableFutureDates: true,
      minDate: CHILD_DEFAULT,
      defaultCalendarMonth: CHILD_DEFAULT,
    }
  }

  return {
    disableFutureDates: true,
    maxDate: ADULT_CUTOFF,
    defaultCalendarMonth: ADULT_CUTOFF,
  }
}

/**
 * Returns TBD status for event date and time:
 * - isDateTbd: true when event name contains "Date TBD" (date + time both unknown)
 * - isTimeTbd: true when event name contains "Date TBD" OR time is between 12:00 AM–5:59 AM (placeholder time)
 */
export function isEventDateTimeTbd(
  time: string,
  eventName: string,
): {
  isDateTbd: boolean
  isTimeTbd: boolean
} {
  const isDateTbd = eventName.toLowerCase().includes('date tbd')
  const hour = dayjs(time, 'h:mm A').hour()
  const isTimeTbd = isDateTbd || (!isNaN(hour) && hour < 6)

  return { isDateTbd, isTimeTbd }
}
